"use client";

import * as cmpstub from "@iabtcf/stub";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import React, {
    createContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import type { Nullable } from "../../@types/generic";
import {
    getUserSessionDataFromNativeApp,
    sendEventToNativeApp,
} from "../../bridge/helpers";
import { getWindow } from "../../helpers/getWindow";
import { trackTcfLoaded } from "../../helpers/loadTracking";
import { initializeUserSession } from "../../helpers/userSession";
import { useCountry } from "../../hooks/useCountry";
import { usePageParams } from "../../hooks/usePageParams";
import { useDayjs } from "../Dayjs/hooks";
import { sendError } from "../ErrorBoundary/helpers";
import { useInitialConfig, useIsWebview } from "../WebviewContext/hooks";
import config from "./config";
import { useContentPass, useUCConsentMode } from "./hooks";
import type { Consent } from "./types";

const CMP = dynamic(() => import("./cmp").then((mod) => mod.CMP), {
    ssr: false,
});

export const ConsentContext = createContext<Consent>({
    consent: false,
    isPay: false,
    openCMP: () => undefined,
});

type Props = {
    children: React.ReactNode;
};

export const ConsentProvider: React.FC<Props> = ({ children }) => {
    const isWebview = useIsWebview();
    const country = useCountry();
    const initialConfig = useInitialConfig();
    const window = getWindow();
    const { pageType } = usePageParams();
    const dayjs = useDayjs();
    const searchParams = useSearchParams();
    const [consent, setConsent] = useState(
        searchParams.get("cmp") === "accepted"
    );
    const [isPay, setIsPay] = useState(false);
    const [isForced, setIsForced] = useState(false);
    const [skipCMP, setSkipCMP] = useState<Nullable<boolean>>(
        isWebview === true ||
            searchParams.get("cmp") === "accepted" ||
            pageType?.isPlainPage
            ? true
            : null
    );
    const {
        init: initContentPass,
        auth: authContentPass,
        remember: rememberContentPass,
    } = useContentPass();
    useUCConsentMode(consent);

    const isUcInitialized = useRef(false);
    const isCoPaInitialized = useRef(false);

    if (window && pageType !== null && !pageType.isAnyForecastPage) {
        // initialize stub api with window.__tcfapi placeholder, to be early available for TCF consumers.
        // Will be replaced by correct __tcfapi later on load time.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        cmpstub();
    }

    const contextValue = useMemo(() => {
        const openCMP = isWebview
            ? (): void => undefined
            : (): void => setIsForced(true);
        return { consent, isPay, openCMP };
    }, [consent, isPay, isWebview]);

    useEffect(() => {
        if (window !== undefined) {
            initializeUserSession(window);
        }
    }, [window]);

    useEffect(() => {
        if (window && isWebview && initialConfig && !isUcInitialized.current) {
            isUcInitialized.current = true;
            setIsPay(initialConfig.isPay ?? false);

            if (initialConfig.ucSettingsId) {
                void (async (): Promise<void> => {
                    try {
                        const userSessionData =
                            await getUserSessionDataFromNativeApp();
                        const Usercentrics =
                            await import("@usercentrics/cmp-browser-sdk").then(
                                (mod) => mod.default
                            );
                        const UC = new Usercentrics(
                            initialConfig.ucSettingsId as string,
                            { userSessionData }
                        );
                        await UC.init();
                        const consentFromUC = UC.areAllConsentsAccepted();
                        setConsent(consentFromUC);
                        sendEventToNativeApp({
                            type: "consentStatus",
                            consent: consentFromUC ? "full" : "none",
                        });
                    } catch (e) {
                        sendError(e);
                        setConsent(false);
                        sendEventToNativeApp({
                            type: "consentStatus",
                            consent: "none",
                        });
                    }
                })();
            } else {
                sendEventToNativeApp({
                    type: "consentStatus",
                    consent: "none",
                });
            }
        }
    }, [isWebview, initialConfig, window]);

    useEffect(() => {
        if (
            isWebview === true ||
            window === undefined ||
            isCoPaInitialized.current
        ) {
            return;
        }
        isCoPaInitialized.current = true;

        if (searchParams.get("cmp") === "accepted") {
            // skip cmp if ?cmp=accepted is present
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSkipCMP(true);
            setConsent(true);
            return;
        }

        let ucTcfStorage: { tcString: Nullable<string>; timestamp: number } = {
            tcString: null,
            timestamp: 0,
        };

        try {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            ucTcfStorage = JSON.parse(
                window.localStorage.getItem("uc_tcf") ??
                    '{ "tcString": null, "timestamp": 0 }'
            );
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("error for tcf api localStorage", e);
        }

        if (ucTcfStorage.tcString) {
            void import("@iabtcf/cmpapi").then((mod) => {
                const cmpApi = new mod.CmpApi(
                    config.cmp.id,
                    config.cmp.version,
                    false
                );

                cmpApi.update(ucTcfStorage.tcString);
                setSkipCMP(true);
                setConsent(true);
                trackTcfLoaded(window, country);
            });

            return;
        }

        setSkipCMP(false);

        void initContentPass().then(() => {
            void authContentPass().then((isLoggedIn) => {
                if (isLoggedIn) {
                    rememberContentPass();
                    setIsPay(true);
                    setSkipCMP(true);
                }
            });
        });
    }, [
        window,
        authContentPass,
        initContentPass,
        isPay,
        rememberContentPass,
        searchParams,
        isWebview,
        dayjs,
        country,
    ]);

    return (
        <ConsentContext value={contextValue}>
            {children}
            {skipCMP === false || isForced ? (
                <CMP
                    setConsent={setConsent}
                    setIsPay={setIsPay}
                    forcedShow={isForced}
                    setIsForced={setIsForced}
                />
            ) : null}
        </ConsentContext>
    );
};
